// Installer Growth Pilot — onboarding form schema.
// Data-driven field definitions consumed by the wizard renderers.

export const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
export const GST_OPTS = ["Inclusive of GST", "Exclusive of GST"];
export const YES_NO = ["Yes", "No"];
export const ACCESS_OPTS = ["Not started", "Ready to grant", "Invitation sent", "Access granted", "Need assistance", "Not applicable"];

export const PRESET_CHARGES = [
  ["battery-cover", "Battery cover or cabinet", "Battery"],
  ["inverter-cover", "Inverter cover", "Electrical"],
  ["safety-bollards", "Safety bollards", "Site works"],
  ["additional-cable-run", "Additional cable run", "Electrical"],
  ["switchboard-upgrade", "Switchboard upgrade", "Electrical"],
  ["additional-switchboard-work", "Additional switchboard work", "Electrical"],
  ["metering-work", "Metering-related work", "Metering"],
  ["double-storey", "Double-storey installation", "Roof / access"],
  ["difficult-roof", "Difficult roof type", "Roof / access"],
  ["restricted-access", "Difficult or restricted access", "Roof / access"],
  ["tile-roof", "Tile-roof surcharge", "Roof / access"],
  ["flat-roof-tilt", "Flat-roof or tilt-frame requirements", "Roof / access"],
  ["scaffolding", "Scaffolding", "Roof / access"],
  ["elevated-work-platform", "Elevated work platform", "Roof / access"],
  ["trenching", "Trenching or underground cable work", "Site works"],
  ["regional-travel", "Regional travel", "Travel"],
  ["extra-travel", "Additional travel beyond the included radius", "Travel"],
  ["three-phase-work", "Three-phase related work", "Electrical"],
  ["backup-circuits", "Backup-circuit work beyond the package inclusion", "Battery"],
  ["consumption-meter", "Consumption meter/CT installation", "Metering"],
  ["relocation", "Relocation of inverter or battery from the standard position", "Site works"],
  ["removal-disposal", "Removal/disposal of an existing solar or battery system", "Site works"],
  ["asbestos", "Asbestos-related limitations or work", "Site works"],
  ["site-inspection", "Site inspection", "Assessment"],
  ["dnsp-fee", "DNSP/network application or metering fee not included in the package", "Metering"],
  ["other-variation", "Other common installation variation", "Other"],
];

export const CHARGE_STATUSES = ["Included", "Fixed charge", "Unit rate", "Price on assessment", "Not applicable"];
export const CHARGE_UNITS = ["each", "per metre", "per hour", "per trip", "per day", "per kW", "per site"];

export const DECLARATIONS = [
  ["authorised", "I am authorised to provide this information and act on behalf of the business."],
  ["accurate", "The information, pricing, licences, accreditations, product details, warranties and policies supplied are accurate and current."],
  ["brandUse", "The business authorises Green Circle Group to use the approved brand assets, package information, pricing, offers and testimonials for preparing and managing the pilot campaigns and sales proposals."],
  ["notifyChanges", "The business will notify Green Circle Group promptly if any supplied price, product, licence, service area, capacity, warranty, promotion or compliance information changes."],
  ["budgetAck", "I acknowledge the minimum proposed Meta advertising budget of $50 per day, paid directly by the business to Meta."],
  ["feeWaiverAck", "I acknowledge that campaign management is waived for the initial four-week pilot only."],
  ["commissionAck", "I acknowledge the proposed sales commission of $200 plus GST per converted sale, subject to the final signed agreement and agreed conversion trigger."],
  ["targetAck", "I understand that the target of reaching a process capable of at least two converted sales per week by the end of the first two weeks is a performance target and is not a guarantee."],
  ["resultsAck", "I understand that results depend on package competitiveness, products, service areas, customer demand, lead quality, customer/site suitability and other market factors."],
  ["privacyAck", "I have read and agree to Green Circle Group's Privacy Policy and applicable onboarding terms."],
];

export const SECURITY_NOTICE =
  "Security notice: Do not enter passwords, payment-card details, one-time security codes, API keys or private access tokens in this form. Account access must be granted through the official sharing or invitation tools provided by Meta, GreenSketch and the relevant platforms.";

const pkgIncludesSolar = (p) => ["Solar only", "Solar plus battery"].includes(p?.category);
const pkgIncludesBattery = (p) => ["Battery only", "Solar plus battery", "Battery add-on/retrofit"].includes(p?.category);

// --------------------------------------------------------- package fields --
export const PACKAGE_GROUPS = [
  {
    title: "General package information",
    fields: [
      { key: "status", label: "Package status", type: "select", options: ["Active", "Temporarily unavailable", "Draft"], required: true, half: true },
      { key: "category", label: "Package category", type: "select", options: ["Solar only", "Battery only", "Solar plus battery", "Battery add-on/retrofit", "Other"], required: true, half: true },
      { key: "internalName", label: "Internal package name", half: true, help: "Your own reference name." },
      { key: "customerName", label: "Customer-facing package name", required: true, half: true },
      { key: "regions", label: "States/service regions where available", half: true, placeholder: "e.g. NSW — Sydney metro" },
      { key: "propertyType", label: "Suitable property/customer type", half: true, placeholder: "e.g. Single-storey residential" },
      { key: "phase", label: "Phase", type: "select", options: ["Single phase", "Three phase", "Both"], half: true },
      { key: "leadTime", label: "Estimated installation lead time", half: true, placeholder: "e.g. 2–3 weeks" },
      { key: "shortDesc", label: "Short customer-facing description", type: "textarea" },
      { key: "inclusions", label: "Standard inclusions", type: "textarea" },
      { key: "exclusions", label: "Standard exclusions", type: "textarea" },
      { key: "quoteValidity", label: "Quote validity period", half: true, placeholder: "e.g. 14 days" },
      { key: "availabilityStart", label: "Availability start date (if applicable)", type: "date", half: true },
      { key: "availabilityEnd", label: "Availability end date (if applicable)", type: "date", half: true },
    ],
  },
  {
    title: "Solar details",
    showIf: pkgIncludesSolar,
    fields: [
      { key: "sysSizeKw", label: "Nominal system size (kW)", type: "number", half: true },
      { key: "panelBrand", label: "Panel manufacturer", half: true },
      { key: "panelModel", label: "Panel model", half: true },
      { key: "panelWattage", label: "Panel wattage (W)", type: "number", half: true },
      { key: "panelCount", label: "Number of panels", type: "number", half: true },
      { key: "panelColour", label: "Panel colour/appearance (if relevant)", half: true },
      { key: "panelProductWarranty", label: "Panel product warranty", half: true, placeholder: "e.g. 25 years" },
      { key: "panelPerfWarranty", label: "Panel performance warranty", half: true, placeholder: "e.g. 30 years / 87.4%" },
      { key: "inverterBrand", label: "Inverter manufacturer", half: true },
      { key: "inverterModel", label: "Inverter model", half: true },
      { key: "inverterCapacity", label: "Inverter capacity", half: true, placeholder: "e.g. 5 kW" },
      { key: "inverterPhase", label: "Inverter phase", type: "select", options: ["Single phase", "Three phase"], half: true },
      { key: "inverterWarranty", label: "Inverter warranty", half: true },
      { key: "mounting", label: "Mounting system brand/type", half: true },
      { key: "monitoring", label: "Monitoring platform/app", half: true },
      { key: "exportLimits", label: "Export-limit assumptions / network limitations", type: "textarea" },
    ],
  },
  {
    title: "Battery details",
    showIf: pkgIncludesBattery,
    fields: [
      { key: "battBrand", label: "Battery manufacturer", half: true },
      { key: "battModel", label: "Battery model", half: true },
      { key: "battNominalKwh", label: "Nominal capacity (kWh)", type: "number", half: true },
      { key: "battUsableKwh", label: "Usable capacity (kWh)", type: "number", half: true },
      { key: "battModules", label: "Number of battery modules", type: "number", half: true },
      { key: "battPowerKw", label: "Battery power rating (kW)", type: "number", half: true },
      { key: "battInverter", label: "Hybrid/separate battery inverter make & model", half: true },
      { key: "battIndoorOutdoor", label: "Installation suitability", type: "select", options: ["Indoor", "Outdoor", "Both"], half: true },
      { key: "backupIncluded", label: "Backup capability included", type: "yesno", half: true },
      { key: "backupCircuits", label: "Backup circuits included (number/type)", half: true },
      { key: "blackStart", label: "Black-start capability (if relevant)", half: true },
      { key: "battWarranty", label: "Battery warranty & throughput/cycle limit", half: true },
      { key: "battMonitoring", label: "Monitoring platform/app", half: true },
      { key: "vpp", label: "VPP compatibility (if relevant)", half: true },
    ],
  },
  {
    title: "Pricing",
    fields: [
      { key: "landingPrice", label: "Client landing price (AUD)", type: "number", required: true, half: true, help: "The price Green Circle Group sells at." },
      { key: "landingGst", label: "Landing price GST treatment", type: "select", options: GST_OPTS, required: true, half: true },
      { key: "retailPrice", label: "Recommended customer retail price (AUD)", type: "number", required: true, half: true },
      { key: "retailGst", label: "Retail price GST treatment", type: "select", options: GST_OPTS, defaultValue: "Inclusive of GST", half: true },
      { key: "minPrice", label: "Minimum authorised customer price (if different)", type: "number", half: true },
      { key: "maxDiscount", label: "Max discount without approval", half: true, placeholder: "e.g. $200 or 5%" },
      { key: "pricingApprover", label: "Approval contact for non-standard pricing", half: true },
      { key: "stcsIncluded", label: "Are STCs included in the price?", type: "yesno", half: true },
      { key: "stcAssumptions", label: "STC zone / deeming assumptions", half: true },
      { key: "rebateIncluded", label: "State rebate/incentive included?", type: "yesno", half: true },
      { key: "rebateDetails", label: "Rebate name & eligibility conditions", half: true },
      { key: "depositType", label: "Customer deposit type", type: "select", options: ["Fixed amount", "Percentage"], half: true },
      { key: "depositValue", label: "Deposit amount / percentage", half: true, placeholder: "e.g. 500 or 10%" },
      { key: "paymentStructure", label: "Progress & final payment structure", type: "textarea" },
      { key: "financeAvailable", label: "Finance/payment plan available?", type: "yesno", half: true, help: "Connect plans in Step 6." },
      { key: "salesNotes", label: "Package-specific sales notes", type: "textarea" },
    ],
  },
  {
    title: "Product approval & substitutions",
    fields: [
      { key: "productsApproved", label: "All nominated products have the approvals required for sale and installation in the applicable location", type: "yesno" },
      { key: "substitutes", label: "Permitted substitute panel, inverter or battery products", type: "textarea" },
      { key: "substitutionApprover", label: "Who must approve a substitution", half: true },
      { key: "unavailableProcedure", label: "Procedure if a product is unavailable after the customer accepts", type: "textarea" },
    ],
  },
];

// Regions per state for the state-first serviceable-areas picker.
export const REGIONS_BY_STATE = {
  NSW: ["Sydney Metro", "Central Coast", "Newcastle & Hunter", "Wollongong & Illawarra", "North Coast", "Coffs Harbour–Grafton", "New England & North West", "Orana & Far West", "Riverina", "Murray", "South East & Tablelands"],
  VIC: ["Melbourne Metro", "Geelong & Surf Coast", "Ballarat & Central Highlands", "Bendigo & Loddon", "Gippsland", "Shepparton & Goulburn Valley", "Wimmera", "Mildura & Mallee", "Great South Coast", "North East"],
  QLD: ["Brisbane Metro", "Gold Coast", "Sunshine Coast", "Moreton Bay", "Logan & Ipswich", "Toowoomba & Darling Downs", "Wide Bay–Burnett", "Rockhampton & Capricorn", "Mackay–Isaac–Whitsunday", "Townsville & North QLD", "Cairns & Far North", "Central West & Outback"],
  SA: ["Adelaide Metro", "Barossa & Yorke", "Fleurieu Peninsula", "Murraylands", "Riverland", "Eyre Peninsula", "Spencer Gulf & Port Augusta", "South East (Mount Gambier)"],
  WA: ["Perth Metro", "Peel & Mandurah", "South West (Bunbury)", "Great Southern", "Wheatbelt", "Mid West & Geraldton", "Goldfields–Esperance", "Pilbara", "Kimberley"],
  TAS: ["Hobart & South", "Launceston & North", "North West Coast (Devonport–Burnie)", "East Coast"],
  NT: ["Darwin & Palmerston", "Katherine", "Alice Springs & Central Australia"],
  ACT: ["Canberra & ACT"],
};

// -------------------------------------------------------------- steps ------
// STAGE 1 — quick client-completed onboarding (minimal, low-friction).
// Packages, additional charges, brand assets, sales/payments detail and the
// full platform-access checklist are intentionally NOT collected here — that
// detailed information is Stage 2, completed by the Green Circle Group team
// in the CRM. The hidden editors (PackagesEditor, ChargesEditor) and their
// schemas (PACKAGE_GROUPS, PRESET_CHARGES) are preserved for Stage 2.
export const STEPS = [
  {
    id: 1, name: "Primary Contact",
    sections: [
      {
        title: "Primary contact",
        fields: [
          { key: "firstName", label: "First name", required: true, half: true },
          { key: "lastName", label: "Last name", required: true, half: true },
          { key: "position", label: "Position/title", required: true, half: true },
          { key: "email", label: "Direct email", type: "email", required: true, half: true, help: "Your draft is linked to this address for save & resume." },
          { key: "phone", label: "Direct phone/mobile", type: "tel", required: true, half: true, help: "Australian (e.g. 0412 345 678) or international (e.g. +61 …) format. We'll SMS a verification code to this number before you submit." },
        ],
      },
    ],
  },
  {
    id: 2, name: "Business Details",
    sections: [
      {
        title: "Business details",
        fields: [
          { key: "legalName", label: "Legal entity name", required: true, half: true },
          { key: "tradingName", label: "Trading/business name", required: true, half: true },
          { key: "abn", label: "ABN", required: true, half: true, help: "11-digit Australian Business Number.", placeholder: "e.g. 51 824 753 556" },
          { key: "website", label: "Website URL", type: "url", required: true, half: true },
          { key: "yearsInOperation", label: "Years in operation", type: "number", required: true, half: true },
          { key: "registeredAddress", label: "Registered business address", type: "textarea", required: true },
          { key: "description", label: "Short business description", type: "textarea", required: true, maxLen: 1000, help: "A sentence or two about what you do. Maximum 1,000 characters." },
        ],
      },
    ],
  },
  {
    id: 3, name: "Serviceable Areas",
    sections: [{ title: "Where do you install?", areas: true }],
    extraFields: [
      { key: "excludedAreas", label: "Areas specifically excluded (optional)", type: "textarea" },
    ],
  },
  {
    id: 4, name: "Accreditation",
    sections: [
      {
        title: "Installer accreditation",
        intro: "Just two quick questions — no evidence uploads needed.",
        fields: [
          { key: "saaAccredited", label: "Are you an SAA accredited installer?", type: "yesno", required: true },
          { key: "saaFirstName", label: "Accredited installer's first name", required: true, half: true, showIf: (d) => d.saaAccredited === "Yes" },
          { key: "saaLastName", label: "Accredited installer's last name", required: true, half: true, showIf: (d) => d.saaAccredited === "Yes" },
          { key: "saaNumber", label: "SAA accreditation number", required: true, half: true, showIf: (d) => d.saaAccredited === "Yes" },
          { key: "saaExpiry", label: "SAA accreditation expiry date", type: "date", required: true, half: true, showIf: (d) => d.saaAccredited === "Yes" },
          { key: "netccApproved", label: "Are you an NETCC Approved Installer?", type: "yesno", required: true },
        ],
      },
    ],
  },
  {
    id: 5, name: "Online & Proposals",
    sections: [
      {
        title: "Social media & website",
        intro: "Only enter details you currently have available — leave the rest blank. Links you'll remember off the top of your head are perfect; we can collect the rest later.",
        fields: [
          { key: "fbPageUrl", label: "Facebook page link", type: "url", half: true, placeholder: "https://facebook.com/yourbusiness" },
          { key: "igUrl", label: "Instagram page link", type: "url", half: true, placeholder: "https://instagram.com/yourbusiness" },
        ],
      },
      {
        title: "Proposals & sales tools",
        fields: [
          { key: "proposalTool", label: "What do you use for proposals to customers?", type: "radio", required: true, options: ["Pylon", "OpenSolar", "GreenSketch", "Other"] },
          { key: "proposalToolOther", label: "Which tool?", half: true, showIf: (d) => d.proposalTool === "Other" },
          { key: "proposalAccess", label: "Will you create access for our salesperson to generate proposals on your behalf?", type: "yesno" },
          { key: "dedicatedEmail", label: "Can you create a dedicated email address on your domain (e.g. tony@ or sam@yourbusiness.com.au) that we could use for the sales process, if you choose us to handle sales for you?", type: "yesno" },
        ],
      },
    ],
  },
  {
    id: 6, name: "Campaign Essentials",
    sections: [
      {
        title: "Campaign essentials",
        fields: [
          { key: "campaignStart", label: "Proposed campaign start date", type: "date", half: true },
          { key: "priority", label: "Campaign priority", type: "select", required: true, options: ["Solar", "Battery", "Combined solar + battery"], half: true },
          { key: "dailyBudget", label: "Planned starting daily budget (AUD)", type: "number", required: true, half: true, help: "Minimum $50/day for this pilot — paid by you directly to Meta through your own ad account." },
          { key: "promotion", label: "Current promotion or special offer (optional)", type: "textarea" },
        ],
      },
    ],
  },
  { id: 7, name: "Review & Submit", review: true },
];

// ABN checksum — mirrors the backend validation
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
export function abnValid(abn) {
  const digits = (abn || "").replace(/\D/g, "");
  if (digits.length !== 11) return false;
  const nums = digits.split("").map(Number);
  nums[0] -= 1;
  return nums.reduce((sum, n, i) => sum + n * ABN_WEIGHTS[i], 0) % 89 === 0;
}

export const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const PHONE_RE = /^(\+?\d[\d\s\-()]{6,19})$/;

// Values that must never be entered (Step 7 free-text etc.)
const SECRET_RE = /(BEGIN [A-Z ]*PRIVATE KEY|api[_-]?key\s*[:=]|password\s*[:=]|secret\s*[:=]|token\s*[:=])/i;
const CARD_RE = /\b(?:\d[ -]?){13,19}\b/;
export function looksLikeSecret(v) {
  return typeof v === "string" && (SECRET_RE.test(v) || CARD_RE.test(v));
}

// Per-step client-side validation. Returns {fieldKey: message}
export function validateStep(stepId, data) {
  const errors = {};
  const d = data[`step${stepId}`] || {};
  const step = STEPS.find((s) => s.id === stepId);
  if (!step) return errors;

  const checkField = (f, ctx) => {
    if (f.showIf && !f.showIf(ctx)) return;
    const v = ctx[f.key];
    const empty = v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);
    if (f.required && empty) {
      errors[f.key] = `${f.label.replace(/\s*\(optional\)\s*/i, "")} is required`;
      return;
    }
    if (empty) return;
    if (f.type === "email" && !EMAIL_RE.test(String(v).trim())) errors[f.key] = "Enter a valid email address";
    if (f.type === "tel" && !PHONE_RE.test(String(v).trim())) errors[f.key] = "Enter a valid phone number";
    if (f.key === "abn" && !abnValid(v)) errors[f.key] = "Enter a valid 11-digit ABN";
    if (f.key === "acn" && !/^\d{9}$/.test(String(v).replace(/\D/g, ""))) errors[f.key] = "ACN must be 9 digits";
    if (f.maxLen && String(v).length > f.maxLen) errors[f.key] = `Maximum ${f.maxLen} characters`;
    if (stepId === 5 && looksLikeSecret(v)) errors[f.key] = "Never enter passwords, card numbers or access tokens — grant access via the platform's sharing tools";
    if (f.key === "dailyBudget") {
      const n = parseFloat(String(v).replace(/,/g, ""));
      if (isNaN(n) || n < 50) errors[f.key] = "The minimum Meta advertising budget for this pilot is $50 per day";
    }
  };

  for (const sec of step.sections || []) {
    for (const f of sec.fields || []) checkField(f, d);
  }

  if (stepId === 3) {
    if (!(d.regions || []).length) errors.regions = "Select at least one region to service";
  }
  if (stepId === 7) {
    const decls = d.declarations || {};
    for (const [k] of DECLARATIONS) if (!decls[k]) { errors.declarations = "All declarations must be accepted before submitting"; break; }
    if (!(d.signerName || "").trim()) errors.signerName = "Authorised person's full name is required";
    if (!(d.signerTitle || "").trim()) errors.signerTitle = "Position/title is required";
    if (!(d.signature || "").trim()) errors.signature = "Type your full name as an electronic acknowledgement/signature";
  }
  return errors;
}

export function validateAll(data) {
  const all = {};
  for (const s of STEPS) {
    const errs = validateStep(s.id, data);
    if (Object.keys(errs).length) all[s.id] = errs;
  }
  return all;
}
